/**
 * V4 scenario schedule <-> V3 availability grid conversion.
 *
 * V3 uses a boolean[7][48] grid (day 0 = Monday, slot 0 = 00:00-00:30); V4 uses
 * number[7], one 48-bit mask per day with bit `i` (LSB) = slot `i`. Indexing is
 * positional and identical, so it's a straight per-day bitmask encode/decode.
 * Masks use Math.pow arithmetic since JS bitwise ops are 32-bit; values stay
 * below 2^48 < MAX_SAFE_INTEGER, so integers are exact.
 *
 * The editor grid is wall-clock time in the account timezone; V4 evaluates the
 * masks in UTC, so encode/decode also rotate the flattened 336-slot week by
 * that zone's offset. Sampled at encode time — V4 has no timezone field, and
 * one weekly mask can't express both sides of a DST flip, so a schedule saved
 * in summer reads an hour off in winter until re-saved.
 */

import dayjs from 'dayjs';

export const SLOTS_PER_DAY = 48;
export const DAYS_PER_WEEK = 7;
export const SLOTS_PER_WEEK = SLOTS_PER_DAY * DAYS_PER_WEEK;

// "Always active" — all 48 slots set. 2^48-1 fits any int64 column (2^53-1 overflowed the V4 validator).
export const ALL_SLOTS_MASK = Math.pow(2, SLOTS_PER_DAY) - 1;
export const DEFAULT_V4_SCHEDULE = Array.from(Array(DAYS_PER_WEEK), () => ALL_SLOTS_MASK);

// Treat a non-7-finite-mask array as absent so callers fall back to a default
// instead of decoding a malformed backend response into partial data.
export function isValidScheduleMasks(masks) {
    return Array.isArray(masks)
        && masks.length === DAYS_PER_WEEK
        && masks.every(m => typeof m === 'number' && Number.isFinite(m));
}

// Reject a short/ragged grid that would otherwise encode to a bogus mask.
export function isValidScheduleGrid(grid) {
    return Array.isArray(grid)
        && grid.length === DAYS_PER_WEEK
        && grid.every(day => Array.isArray(day) && day.length === SLOTS_PER_DAY);
}

// boolean[48] -> 48-bit integer mask.
export function slotsToMask(daySlots) {
    const slots = Array.isArray(daySlots) ? daySlots : [];
    let mask = 0;
    for (let i = 0; i < SLOTS_PER_DAY; i += 1) {
        if (slots[i]) mask += Math.pow(2, i);
    }
    return mask;
}

// 48-bit integer mask -> boolean[48]. Real booleans (not 1/0) so it deep-equals
// the WeekAvailability presets under JSON.stringify.
export function maskToSlots(mask) {
    const n = Number(mask) || 0;
    return Array.from(Array(SLOTS_PER_DAY), (_, i) => Math.floor(n / Math.pow(2, i)) % 2 === 1);
}

// Slots `timezone` runs ahead of UTC right now (UTC+2 -> 4); missing/unknown
// zone falls back to the browser's. :45 offsets round to the nearest slot.
export function utcOffsetSlots(timezone) {
    let minutes;
    try {
        minutes = timezone ? dayjs().tz(timezone).utcOffset() : dayjs().utcOffset();
    } catch (e) {
        minutes = dayjs().utcOffset();
    }
    return Math.round((minutes || 0) / 30);
}

// Move every slot `delta` slots later (negative = earlier), wrapping across the
// Sunday->Monday boundary. Real booleans, as maskToSlots.
function rotateGrid(grid, delta) {
    const shift = ((delta % SLOTS_PER_WEEK) + SLOTS_PER_WEEK) % SLOTS_PER_WEEK;
    if (!shift) return grid.map(day => day.map(slot => !!slot));
    return Array.from(Array(DAYS_PER_WEEK), (_, day) =>
        Array.from(Array(SLOTS_PER_DAY), (_, slot) => {
            const from = (day * SLOTS_PER_DAY + slot - shift + SLOTS_PER_WEEK) % SLOTS_PER_WEEK;
            return !!grid[Math.floor(from / SLOTS_PER_DAY)][from % SLOTS_PER_DAY];
        })
    );
}

// Local grid -> UTC masks: 08:00 in UTC+2 lands on bit 12 (06:00), not 16.
export function encodeV4Schedule(grid, timezone) {
    return rotateGrid(grid, -utcOffsetSlots(timezone)).map(slotsToMask);
}

// UTC masks -> local grid; exact inverse of encodeV4Schedule for the same zone.
export function decodeV4Schedule(masks, timezone) {
    return rotateGrid(masks.map(maskToSlots), utcOffsetSlots(timezone));
}
